import {
  Box,
  ButtonBase,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material';
import { useMemo, useRef, useState } from 'react';

import {
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
} from 'helpers/datetime';

import DownArrowIcon from 'media/images/icons/downarrow.svg?react';

import type { LocatedDateRangerPickerValue } from '.';

interface Props {
  onChange: (range: [Date, Date]) => void;
  value: LocatedDateRangerPickerValue;
}

const Presets = ({ onChange, value: { start, end } }: Props) => {
  const [open, setOpen] = useState(false);
  const anchorPreset = useRef<HTMLDivElement | null>(null);

  // Presets start and end dates are always at the start of the day.
  const presets: Array<{ label: string; value: [Date, Date] }> = useMemo(
    () => [
      {
        label: 'Last 7 Days',
        value: [startOfDay(subDays(new Date(), 6)), startOfDay(new Date())],
      },
      {
        label: 'Last 30 Days',
        value: [startOfDay(subDays(new Date(), 29)), startOfDay(new Date())],
      },
      {
        label: 'Last 90 Days',
        value: [startOfDay(subDays(new Date(), 89)), startOfDay(new Date())],
      },
      {
        label: 'Last 12 Months',
        value: [
          startOfMonth(subMonths(new Date(), 11)),
          startOfDay(new Date()),
        ],
      },
      { label: 'This Year', value: [startOfYear(), startOfDay(new Date())] },
    ],
    [],
  );

  const active =
    start && end
      ? presets.find(
          ({ value: [presetStart, presetEnd] }) =>
            isSameDay(start, presetStart) && isSameDay(end, presetEnd),
        )
      : null;

  return (
    <Box display="flex" ref={anchorPreset}>
      <ButtonBase
        color="primary"
        onClick={() => setOpen(true)}
        sx={(theme) => ({
          color: theme.palette.primary.main,
          fontSize: '12px',
          fontWeight: 500,
        })}
      >
        {active?.label ?? 'Preset intervals'}

        <Box alignItems="center" display="flex" fontSize="16px">
          <DownArrowIcon />
        </Box>
      </ButtonBase>

      <Popper
        anchorEl={anchorPreset.current}
        disablePortal
        open={open}
        placement="top-start"
        transition
      >
        {({ placement, TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <div>
              <ClickAwayListener
                onClickAway={(event) => {
                  if (
                    anchorPreset.current &&
                    anchorPreset.current.contains(event.target as HTMLElement)
                  )
                    return;

                  setOpen(false);
                }}
              >
                <Paper sx={{ padding: 1 }}>
                  <MenuList
                    onKeyDown={(event) => {
                      if (event.key === 'Tab') {
                        event.preventDefault();
                        setOpen(false);
                      } else if (event.key === 'Escape') {
                        setOpen(false);
                      }
                    }}
                  >
                    {presets.map(({ label, value }) => (
                      <MenuItem
                        key={label}
                        onClick={() => {
                          setOpen(false);
                          onChange(value);
                        }}
                        selected={active?.label === label}
                      >
                        {label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Paper>
              </ClickAwayListener>
            </div>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

export default Presets;
